import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, ShieldCheck, CheckCircle, KeyRound, Mail } from 'lucide-react';

type Step = 'verify' | 'password' | 'success';

const OTP_LENGTH = 8;

const SetupPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const emailFromUrl = searchParams.get('email') || '';
  const isResetMode = searchParams.get('mode') === 'reset';

  const [step, setStep] = useState<Step>('verify');
  const [email, setEmail] = useState(emailFromUrl);
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [initializing, setInitializing] = useState(true);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    let mounted = true;

    const checkExistingSession = async () => {
      const hash = window.location.hash;
      const hasTokenInHash = hash.includes('access_token') || hash.includes('refresh_token');

      if (hasTokenInHash) {
        const maxAttempts = 20;
        for (let i = 0; i < maxAttempts; i++) {
          if (!mounted) return;
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            if (mounted) {
              setStep('password');
              setInitializing(false);
            }
            return;
          }
          await new Promise(r => setTimeout(r, 500));
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session && mounted) {
        const sessionEmail = session.user?.email?.toLowerCase();
        const targetEmail = emailFromUrl?.toLowerCase();
        if (targetEmail && sessionEmail !== targetEmail) {
          await supabase.auth.signOut();
        } else {
          setStep('password');
        }
      }

      if (mounted) setInitializing(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (session && step === 'verify') {
        setStep('password');
        setInitializing(false);
      }
    });

    checkExistingSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const otpValue = otpDigits.join('');

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').split('').slice(0, OTP_LENGTH);
      const newDigits = [...otpDigits];
      digits.forEach((d, i) => {
        if (index + i < OTP_LENGTH) newDigits[index + i] = d;
      });
      setOtpDigits(newDigits);
      const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    const digit = value.replace(/\D/g, '');
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newDigits = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((d, i) => { newDigits[i] = d; });
    setOtpDigits(newDigits);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (otpValue.length !== OTP_LENGTH) {
      setError('Please enter the full verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    const { error: verifyErr } = await supabase.auth.verifyOtp({
      email: email.toLowerCase(),
      token: otpValue,
      type: 'magiclink',
    });

    if (verifyErr) {
      setError(
        verifyErr.message.includes('expired')
          ? isResetMode
            ? 'This code has expired. Please go back and request a new one.'
            : 'This code has expired. Please ask your admin to resend the invitation.'
          : verifyErr.message
      );
      setIsLoading(false);
      return;
    }

    setStep('password');
    setIsLoading(false);
  };

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const allChecksPassed = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allChecksPassed) {
      setError('Please meet all password requirements');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    const { error: updateErr } = await supabase.auth.updateUser({ password });

    if (updateErr) {
      setError(updateErr.message);
      setIsLoading(false);
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    const userEmail = session?.user?.email?.toLowerCase() || email.toLowerCase();
    if (userEmail) {
      await supabase
        .from('client_users')
        .update({ invitation_status: 'accepted' })
        .eq('email', userEmail);
    }

    setStep('success');
    setIsLoading(false);
    setTimeout(() => navigate('/'), 2000);
  };

  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="w-8 h-8 border-2 border-[#1875FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {isResetMode ? 'Password Reset Successfully' : 'Password Set Successfully'}
            </h1>
            <p className="text-gray-600">Redirecting you to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
              <img src="/salespad logo.svg" alt="SalesPad" className="w-16 h-16" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isResetMode ? 'Reset Your Password' : 'Welcome to SalesPad'}
            </h1>
            <p className="text-gray-600 text-sm">
              {step === 'verify'
                ? isResetMode
                  ? 'Enter the verification code we sent to reset your password'
                  : 'Enter the verification code from your email'
                : isResetMode
                  ? 'Set your new password'
                  : 'Create your password to get started'}
            </p>
          </div>

          {step === 'verify' && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              {!emailFromUrl && (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 pl-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent transition-colors"
                      placeholder="Enter your email"
                    />
                    <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              )}

              {emailFromUrl && (
                <div className="bg-gray-50 rounded-lg px-4 py-3 flex items-center gap-3">
                  <Mail size={18} className="text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate">{emailFromUrl}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Verification Code
                </label>
                <div className="flex gap-1.5 justify-center" onPaste={handleOtpPaste}>
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => { inputRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="w-10 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent transition-colors"
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Enter the verification code from your email
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || otpValue.length !== OTP_LENGTH}
                className="w-full bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white py-3 px-4 rounded-lg font-medium hover:shadow-lg hover:scale-[1.02] focus:ring-2 focus:ring-[#1875FF]/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:scale-100 transition-all flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <KeyRound size={20} />
                    <span>Verify Code</span>
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-[#1875FF] hover:text-[#1875FF]/80 transition-colors"
                >
                  Already have a password? Sign in
                </button>
              </div>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={handleSetPassword} className="space-y-5">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent transition-colors pr-12"
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {password.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                  {[
                    { key: 'length', label: 'At least 8 characters' },
                    { key: 'uppercase', label: 'One uppercase letter' },
                    { key: 'lowercase', label: 'One lowercase letter' },
                    { key: 'number', label: 'One number' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                        passwordChecks[key as keyof typeof passwordChecks] ? 'bg-green-500' : 'bg-gray-300'
                      }`}>
                        {passwordChecks[key as keyof typeof passwordChecks] && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className={passwordChecks[key as keyof typeof passwordChecks] ? 'text-green-700' : 'text-gray-500'}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-transparent transition-colors ${
                    confirmPassword.length > 0
                      ? passwordsMatch ? 'border-green-300' : 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !allChecksPassed || !passwordsMatch}
                className="w-full bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white py-3 px-4 rounded-lg font-medium hover:shadow-lg hover:scale-[1.02] focus:ring-2 focus:ring-[#1875FF]/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:scale-100 transition-all flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <ShieldCheck size={20} />
                    <span>{isResetMode ? 'Reset Password' : 'Create Account'}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupPasswordPage;
